import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Pusher from "pusher-js";

// Services
import {
	getSessionStatus,
	cancelSession,
	unlockSession,
} from "../../../services/api";

// Utils
import {
	dateToTime,
	isWithinUnlockWindow,
	getTimeUntilUnlock,
	formatTime,
} from "../../../utils/timeFormatter";

// Hooks
import { useCountdownTimer } from "../../../hooks/useCountdownTimer";

// Components
import { QRCodeCanvas } from "qrcode.react";
import SessionCancel from "./SessionCancel";
import ActiveSession from "./ActiveSession";
import logo from "../../../images/logo.png";
import { confirmActionIcon, Icons } from "../../../components/Icons";
import ConfirmAction from "../../../components/ConfirmAction";
import FinishedSession from "./FinishedSession";
import AbandonedSession from "./AbandonedSession";
import SessionLoadingState from "../../../components/SessionLoadingState";
import PaymentProcessingState from "../../../components/PaymentProcessingState";

export default function Reservation() {
	const navigate = useNavigate();
	const { sessionId } = useParams();
	const [errorMessage, setErrorMessage] = useState(null);
	const [pageLoading, setPageLoading] = useState(false);
	const [session, setSession] = useState(null);
	const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
	const [user, setUser] = useState(null);
	const [isCancelled, setIsCancelled] = useState(false);
	const [activeSession, setActiveSession] = useState(false);
	const [isFinished, setIsFinished] = useState(false);
	const [isAbandoned, setIsAbandoned] = useState(false);
	const [isPendingPayment, setIsPendingPayment] = useState(false);

	const unlockExpiresAt = session?.unlockExpiresAt
		? new Date(session.unlockExpiresAt)
		: null;
	const unlockTimeoutSeconds = unlockExpiresAt
		? Math.max(0, Math.floor((unlockExpiresAt - new Date()) / 1000))
		: 0;

	// Use countdown timer hook for unlock timeout - must be called unconditionally
	const unlockTimeRemaining = useCountdownTimer(
		unlockTimeoutSeconds > 0 ? unlockTimeoutSeconds : 0,
		() => {
			// When unlock timeout expires, refresh session status
			const isWalkupSession = session?.mode === "walkup";
			if (
				unlockTimeoutSeconds > 0 &&
				isWalkupSession &&
				session?.status === "new"
			) {
				setErrorMessage(
					"Unlock timeout expired. The session has been abandoned."
				);
				// Refresh session status to get updated state
				getSessionStatus(sessionId)
					.then((response) => {
						setSession(response.session);
						if (response.session.status === "abandoned") {
							setIsAbandoned(true);
						}
					})
					.catch((error) => {
						console.error("Failed to refresh session after timeout:", error);
					});
			}
		}
	);

	useEffect(() => {
		// Prevent going back or forward
		const handlePopState = () =>
			window.history.pushState(null, "", window.location.href);

		window.history.pushState(null, "", window.location.href);
		window.addEventListener("popstate", handlePopState);

		const fetchSession = async () => {
			try {
				setPageLoading(true);
				const response = await getSessionStatus(sessionId);
				setSession(response.session);
				setUser(response.user);
				if (response.session.status === "cancelled") {
					setIsCancelled(true);
				}
			} catch (error) {
				setErrorMessage(error.message);
				if (error.message === "Session not found") {
					navigate("/0500550214/reservation/time-scheduler");
				}
			} finally {
				setPageLoading(false);
			}
		};
		setShowCancelConfirmation(false);
		fetchSession();

		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, [sessionId, navigate]);

	// Check if session status
	useEffect(() => {
		const checkSessionStatus = async () => {
			try {
				const res = await getSessionStatus(sessionId);
				if (res.session.status === "active") {
					setActiveSession(true);
				} else if (res.session.status === "pending_payment") {
					setIsPendingPayment(true);
				} else if (res.session.status === "finished") {
					setIsFinished(true);
				} else if (res.session.status === "abandoned") {
					setIsAbandoned(true);
				}
			} catch (error) {
				console.log(error);
			}
		};
		checkSessionStatus();

		const intervalId = setInterval(checkSessionStatus, 30000);
		return () => clearInterval(intervalId);
	}, [sessionId, navigate]);

	useEffect(() => {
		let pusher;
		let channel;

		const initializePusher = () => {
			try {
				// Initialize Pusher with better configuration
				pusher = new Pusher("4153721756ecababd85d", {
					cluster: "us2",
					forceTLS: true,
					enabledTransports: ["ws", "wss"],
					disabledTransports: [],
				});

				// Add connection event listeners
				pusher.connection.bind("connected", () => {
					console.log("✅ Pusher connected successfully");
				});

				pusher.connection.bind("disconnected", () => {
					console.log("❌ Pusher disconnected");
				});

				pusher.connection.bind("error", (error) => {
					console.error("❌ Pusher connection error:", error);
				});

				// Subscribe to a channel specific to this session
				channel = pusher.subscribe(`session-${sessionId}`);
				console.log("📡 Subscribed to session channel:", sessionId);

				// Listen for session status updates
				channel.bind("session-update", async (data) => {
					console.log("📨 Session update received:", data);
					if (
						data.status === "active" ||
						data.status === "pending_payment" ||
						data.status === "finished" ||
						data.status === "abandoned"
					) {
						try {
							// Fetch the latest session data when status changes
							const res = await getSessionStatus(sessionId);
							setSession(res.session);

							// Then update the status
							if (data.status === "active") {
								console.log("🟢 Session is now active");
								setActiveSession(true);
							} else if (data.status === "pending_payment") {
								console.log("💳 Processing payment...");
								setIsPendingPayment(true);
							} else if (data.status === "finished") {
								console.log("✅ Session finished");
								setIsPendingPayment(false); // Clear pending state
								setIsFinished(true);
							} else if (data.status === "abandoned") {
								console.log("⚠️ Session abandoned");
								setIsAbandoned(true);
							}
						} catch (error) {
							console.error("Error fetching updated session data:", error);
						}
					} else if (data.status === "test") {
						console.log("🧪 Test event received");
					}
				});

				channel.bind("pusher:subscription_succeeded", () => {
					console.log("✅ Successfully subscribed to session channel");
				});

				channel.bind("pusher:subscription_error", (error) => {
					console.error("❌ Subscription error:", error);
				});
			} catch (error) {
				console.error("❌ Failed to initialize Pusher:", error);
			}
		};

		initializePusher();

		// Proper cleanup function for the useEffect
		return () => {
			if (channel) {
				channel.unbind_all();
				pusher.unsubscribe(`session-${sessionId}`);
			}
			if (pusher) {
				pusher.disconnect();
			}
			console.log("🔌 Pusher disconnected and cleaned up");
		};
	}, [sessionId]);

	// Check if this is a walkup session
	const isWalkupSession = session?.mode === "walkup";

	// For walkup sessions, check if ready to unlock (payment complete but not yet unlocked)
	// For regular sessions, check time-based unlock window
	const canUnlockPod = session
		? isWalkupSession
			? session.status === "new" &&
			  (unlockExpiresAt === null || unlockTimeRemaining > 0) // Walkup: ready if payment complete and (no timeout set OR not expired)
			: isWithinUnlockWindow(
					// Regular: time-based
					session.startTime,
					session.endTime,
					session.booth?.location
			  )
		: false;

	const confirmCancellation = async () => {
		try {
			setPageLoading(true);
			await cancelSession(sessionId, user.phoneNumber);
			setIsCancelled(true);
		} catch (error) {
			setShowCancelConfirmation(false);
			setErrorMessage(error.message);
		} finally {
			setPageLoading(false);
		}
	};

	const dismissConfirmation = () => {
		setShowCancelConfirmation(false);
	};

	if (isCancelled) return <SessionCancel sessionData={session} />;

	if (isAbandoned) return <AbandonedSession sessionData={session} />;

	if (isPendingPayment) return <PaymentProcessingState />;

	if (isFinished) return <FinishedSession sessionData={session} />;

	if (activeSession && session) return <ActiveSession sessionData={session} />;

	// Show loading state while fetching session data or cancelling
	if (pageLoading && !session) {
		return <SessionLoadingState message="Loading your session details..." />;
	}

	if (pageLoading && showCancelConfirmation) {
		return <SessionLoadingState message="Cancelling your session..." />;
	}

	const timeUntilUnlock =
		session && !isWalkupSession
			? getTimeUntilUnlock(session.startTime, session.booth?.location)
			: null;

	return (
		<div className="bg-white min-h-screen flex flex-col items-center px-4 py-6">
			{/* Error Message */}
			{errorMessage && (
				<div className="fixed top-0 left-0 right-0 m-8 text-black text-sm text-center py-3 z-50 border border-red-300 rounded-lg bg-red-50 shadow-lg">
					<div className="flex flex-col items-center">
						{Icons.error}
						<span className="mt-2">{errorMessage}</span>
					</div>
				</div>
			)}

			{/* Logo with Sponsor */}
			<div className="flex flex-col items-center mb-6">
				<img
					src={logo}
					alt="Pebble Spaces Logo"
					className="w-40 h-auto object-contain"
				/>
			</div>
			{/* Session Reserved Card */}
			<div className="bg-green-200 bg-opacity-60 rounded-3xl p-6 mb-6 w-full max-w-sm text-center">
				<div className="flex justify-center mb-4">
					<div className="bg-black rounded-full p-2">
						<svg
							className="w-6 h-6 text-white"
							fill="currentColor"
							viewBox="0 0 20 20"
						>
							<path
								fillRule="evenodd"
								d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
								clipRule="evenodd"
							/>
						</svg>
					</div>
				</div>
				<h2 className="text-2xl font-bold text-black mb-2">Session Reserved</h2>
				<p className="text-black text-base">
					{isWalkupSession ? (
						canUnlockPod ? (
							<>
								Your Pebble is ready.
								<br />
								Unlock when you're ready to enter.
							</>
						) : (
							<>Session expired. Please book again.</>
						)
					) : canUnlockPod ? (
						<>Your pod is ready. Unlock when you're ready to enter.</>
					) : (
						<>
							Your pod will be ready soon.
							{timeUntilUnlock && (
								<> Available for unlock in {timeUntilUnlock}.</>
							)}
						</>
					)}
				</p>
			</div>
			{/* Session Details */}
			<div className="w-full max-w-sm mb-4">
				<div className="border-t border-gray-300 pt-4 pb-2">
					<p className="text-lg font-medium text-black text-center">
						{isWalkupSession
							? session?.status === "active"
								? session.endTime
									? `Session ends at ${dateToTime(session.endTime)}`
									: "Pay-by-minute session"
								: null
							: `Reserved Until ${
									session ? dateToTime(session.endTime) : "9:30 AM"
							  }`}
					</p>
				</div>
				<div className="border-b border-gray-300 pb-4">
					<p className="text-base text-gray-600 text-center">
						Pebble {session?.booth?.boothId || "1"} |{" "}
						{session?.booth?.location?.name || "Marriott Hotel Times Square"}
					</p>
				</div>
			</div>
			{/* Check-Out Policy */}
			<div className="w-full max-w-sm mb-8">
				<h3 className="text-2xl font-bold text-black mb-4 text-center">
					Check-Out Policy
				</h3>
				<ul className="space-y-3 text-sm text-gray-700">
					<li className="flex items-start">
						<span className="inline-block w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
						<span>
							Exit and close the door to check out. Infrared sensors detect when
							you leave
						</span>
					</li>
					<li className="flex items-start">
						<span className="inline-block w-2 h-2 bg-black rounded-full mt-2 mr-3 flex-shrink-0"></span>
						<span>
							Your receipt will be texted to you after completing your session
						</span>
					</li>
				</ul>
			</div>

			{/* Cancel Confirmation */}
			<ConfirmAction
				isVisible={showCancelConfirmation}
				onConfirm={confirmCancellation}
				onCancel={dismissConfirmation}
				confirmText="Yes"
				cancelText="No"
				message="Are you sure you want to cancel?"
				confirmColor="red"
				icon={confirmActionIcon.cancel}
			/>

			{/* Unlock Timeout Warning for Walkup Sessions */}
			{isWalkupSession &&
				session?.status === "new" &&
				unlockExpiresAt &&
				unlockTimeRemaining > 0 && (
					<div className="w-full max-w-sm mb-4">
						<div
							className={`border rounded-lg p-3 text-center ${
								unlockTimeRemaining <= 60
									? "bg-red-50 border-red-300 text-red-800"
									: unlockTimeRemaining <= 120
									? "bg-yellow-50 border-yellow-300 text-yellow-800"
									: "bg-blue-50 border-blue-300 text-blue-800"
							}`}
						>
							<p className="text-sm font-semibold mb-1">
								⏱️ Time to unlock: {formatTime(unlockTimeRemaining)}
							</p>
							<p className="text-xs">
								{unlockTimeRemaining <= 60
									? "Hurry! Session will expire soon."
									: "You have 5 minutes to unlock your pod and begin before your session is considered abandoned."}
							</p>
						</div>
					</div>
				)}

			{/* Unlock Button */}
			<div className="w-full max-w-sm mb-6">
				{canUnlockPod ? (
					<button
						className="bg-black text-white w-full py-4 rounded-lg text-base font-semibold transition-colors duration-200 flex items-center justify-center gap-2 hover:bg-gray-800"
						onClick={async () => {
							try {
								setPageLoading(true);
								console.log(
									"🔓 [FRONTEND] Attempting to unlock session:",
									sessionId
								);

								const result = await unlockSession(sessionId);
								console.log("🎉 Booth unlocked successfully:", result);
								// The Pusher event will handle the UI update
							} catch (error) {
								console.error("❌ Unlock request failed:", error);
								setErrorMessage(
									error.message || "Failed to unlock booth. Please try again."
								);
								// If error indicates timeout expired, refresh session
								if (
									error.message?.includes("expired") ||
									error.message?.includes("timeout")
								) {
									getSessionStatus(sessionId)
										.then((response) => {
											setSession(response.session);
											if (response.session.status === "abandoned") {
												setIsAbandoned(true);
											}
										})
										.catch((err) => {
											console.error("Failed to refresh session:", err);
										});
								}
							} finally {
								setPageLoading(false);
							}
						}}
						disabled={pageLoading}
					>
						{pageLoading ? (
							<>
								<div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
								Unlocking...
							</>
						) : (
							"Unlock Your Pod"
						)}
					</button>
				) : (
					<div className="space-y-4">
						{isWalkupSession ? (
							// Walkup session unlock timeout expired
							<div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
								<h3 className="text-lg font-semibold text-red-800 mb-2">
									⏱️ Unlock Timeout Expired
								</h3>
								<p className="text-sm text-red-700 mb-2">
									The unlock window has expired. The $1 fee is non-refundable.
									Please book again if you'd like to use the booth.
								</p>

								<button
									className="bg-black text-white w-full py-4 rounded-lg text-base font-semibold transition-colors duration-200 flex items-center justify-center gap-2 hover:bg-gray-800"
									onClick={() => navigate(`/book/${session?.booth?._id}/`)}
								>
									Book Again
								</button>
							</div>
						) : (
							// Regular session not yet available
							<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
								<h3 className="text-lg font-semibold text-yellow-800 mb-2">
									⏰ Session Not Yet Available
								</h3>
								<p className="text-sm text-yellow-700 mb-2">
									Your session is scheduled to start at{" "}
									{session ? dateToTime(session.startTime) : ""}.
								</p>
								<p className="text-sm text-yellow-700">
									{timeUntilUnlock && (
										<span className="block mt-1 font-medium">
											Available in {timeUntilUnlock}
										</span>
									)}
								</p>
							</div>
						)}
						<button
							className="bg-gray-300 text-gray-500 w-full py-4 rounded-lg text-base font-semibold cursor-not-allowed"
							disabled
						>
							Unlock Your Pod
						</button>
					</div>
				)}
			</div>

			{/* Help Button */}
			<button
				className="w-full max-w-sm bg-white border border-gray-300 rounded-lg py-4 px-6"
				onClick={() => {
					window.open("https://pebblespaces.com/contact", "_blank");
				}}
			>
				<span className="text-base font-semibold text-black">
					Need Help? Report a Problem
				</span>
			</button>

			{/* QR Code (Hidden but kept for functionality) */}
			<div className="hidden">
				<QRCodeCanvas
					id="qr-code"
					value={`checkIn-${sessionId}`}
					size={175}
					bgColor={"#ffffff"}
					fgColor={"#000000"}
					level={"M"}
					includeMargin={true}
				/>
			</div>
		</div>
	);
}
